"use client";

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { apiBaseUrl, gatewayHeaders } from "@/config/api";
import { endSession } from "@/utils/session";

// The session is over: clear everything the browser holds, not just the login
// keys, so nothing from this user is left for whoever signs in next.
const cleanupAndRedirect = endSession;

// fetchBaseQuery expects a trailing slash for relative paths.
const baseUrl = `${apiBaseUrl}/`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    for (const [name, value] of Object.entries(gatewayHeaders())) {
      headers.set(name, value);
    }
    return headers;
  },
  credentials: "include",
});

// A 401 means the 4h access token expired, not that the session is over: the
// refresh cookie lives for 7 days. Trade it for a fresh access token and replay
// the request; only give up (and log out) if the refresh itself is rejected.
//
// `refreshInFlight` is the lock. Without it every request that happens to be in
// flight when the token expires fires its own refresh, and the losers of that
// race replay against a cookie that has already been rotated. Callers that
// arrive during a refresh await the same promise instead.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

// "rejected" means the server told us the session is over; "failed" means the
// refresh never got an answer (network drop, CORS, 5xx). Only the first is
// grounds for logging someone out — treating the second the same way ends the
// session over a blip that says nothing about whether it is still valid.
type RefreshOutcome = "ok" | "rejected" | "failed";

const runRefresh = async (
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2]
): Promise<RefreshOutcome> => {
  // The refresh cookie is named per company in production, and the backend can
  // only resolve that name from the subdomain — which rawBaseQuery attaches as
  // X-Subdomain. Going through it rather than a bare fetch keeps that header
  // (and `credentials: "include"`) in place.
  const result = await rawBaseQuery(
    { url: "/authentication/refresh-token", method: "POST" },
    api,
    extraOptions
  );

  if (!result.error) return "ok";

  const status = result.error.status;
  return status === 401 || status === 403 ? "rejected" : "failed";
};

const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) {
    return result;
  }

  // Never let the refresh call itself recurse into another refresh.
  const requestUrl = typeof args === "string" ? args : args.url;
  if (requestUrl.includes("/authentication/refresh-token")) {
    cleanupAndRedirect();
    return result;
  }

  if (!refreshInFlight) {
    refreshInFlight = runRefresh(api, extraOptions).finally(() => {
      refreshInFlight = null;
    });
  }

  const outcome = await refreshInFlight;

  if (outcome === "rejected") {
    cleanupAndRedirect();
    return result;
  }

  // "failed": hand back the original 401 and leave the session alone. The next
  // request retries the refresh once the network recovers.
  if (outcome === "failed") {
    return result;
  }

  result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    cleanupAndRedirect();
  }

  return result;
};

export const baseApi = createApi({
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    "Ticket",
    "Category",
    "Subcategory",
    "Subcategory",
    "TicketHistory",
    "Note",
    "TicketType",
    "Feedback",
    "Priority",
    "Company",
    "Group",
    "Agent",
    "AgentSkill",
    "Client",
    "UpdateReason",
    "Chat",
    "AgentGroup",
    "WorkShift",
    "Reports",
    "ClientGroup",
    "KbIssue",
    "KbSolution",
    "AIAgent",
    "CompanyInfo",
    "Dashboard",
    "ApiClient",
    "Integration",
    "AuditLog",
    "City",
    "District",
    "LocationAlias",
    "EventType",
    "EventStatus",
    "EventRecord",
    "TtsUsage"
  ],
  endpoints: (builder) => ({}),
});
